import { Injectable } from '@angular/core';
import { AppSettings } from '../../../app-settings';
import {
  FormControl,
  FormGroup,
  Validators,
  ValidatorFn,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { EmailValidator, DomainValidator } from 'commons-validator-js';
import { AuthMaxAgeInputComponent, TimeUnit } from '../auth-max-age-input/auth-max-age-input.component';
import { Duration } from 'luxon';
import { ApplicationSensitivity } from '../../enums/application-sensitivity.enum';

interface FormState {
  value: string;
  disabled: boolean;
}

type InitialValue = string | FormState;

export function validateIf(validator: ValidatorFn, predicate: () => boolean): ValidatorFn {
  return (control: AbstractControl) => {
    if(predicate()) {
      return validator(control)
    } else {
      return null;
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class FormValidationService {
  // Comprehensive set of characters available according to IETF RFC 3986: https://www.ietf.org/rfc/rfc3986.txt
  //
  // pct-encoded = "%" HEXDIG HEXDIG
  //
  // gen-delims  = ":" / "/" / "?" / "#" / "[" / "]" / "@"
  //
  // sub-delims  = "!" / "$" / "&" / "'" / "(" / ")"
  //             / "*" / "+" / "," / ";" / "="
  //
  // unreserved  = ALPHA / DIGIT / "-" / "." / "_" / "~"
  public readonly validUrlPattern = Validators.pattern(/^.+:\/\/[a-zA-Z0-9%\.\\_~!$&'()*+,;=:\/\?\#\[\]\-]+$/);

  // Practical max URL length accepted by most modern browsers
  public readonly validUrlLength = Validators.maxLength(2000);

  public readonly urlValidators = [
    this.validUrlLength,
    this.validUrlPattern,
    this.validUrl,
    this.validStrictProtocol
  ];
  public readonly emailValidator = new EmailValidator();
  public readonly domainValidator = new DomainValidator();

  public getGenericNameControl(initialValue = '') {
    return new FormControl(initialValue, [
      Validators.required,
      Validators.maxLength(AppSettings.GENERIC_FIELD_MAX_LENGTH),
      this.noWhitespaceWrap,
    ]);
  }

  public getEnterpriseNameControl(initialValue = '') {
    return this.getGenericNameControl(initialValue);
  }

  public getApplicationNameControl(initialValue = '') {
    return new FormControl(initialValue, [
      Validators.required,
      Validators.maxLength(AppSettings.GENERIC_FIELD_MAX_LENGTH),
      this.notJustWhitespace,
    ]);
  }

  public getApplicationDescriptionControl(initialValue = '') {
    return new FormControl(initialValue, [
      Validators.maxLength(1000),
    ]);
  }

  public getApplicationAuthMaxAgeGroup() {
    return new FormGroup({
      number: new FormControl(24, [Validators.required, Validators.pattern("^[0-9]*$")]),
      unit: new FormControl(TimeUnit.Hours, [Validators.required]),
    }, this.validAuthMaxAge());
  }

  public getApplicationSensitivityControl(initialValue: ApplicationSensitivity) {
    return new FormControl(initialValue, [Validators.required]);
  }

  public getAccessGroupNameControl(initialValue = '') {
    return new FormControl(initialValue, {
      validators: [
        Validators.required,
        Validators.maxLength(AppSettings.GENERIC_FIELD_MAX_LENGTH),
        this.notJustWhitespace,
      ],
    });
  }

  public getAccessGroupDescriptionControl(initialValue = '') {
    return new FormControl(initialValue, [Validators.maxLength(1000), this.notJustWhitespace]);
  }

  public getUrlControl(initialValue = '', additionalValidators: ValidatorFn[] = []) {
    return new FormControl(initialValue, [
      ...this.urlValidators,
      ...additionalValidators,
    ]);
  }

  public getUserFirstNameControl(initialValue = '') {
    return this.getGenericNameControl(initialValue);
  }

  public getUserLastNameControl(initialValue = '') {
    return this.getGenericNameControl(initialValue);
  }

  /**
   * Create a form control representing a select with a default placeholder value, where the placeholder is not valid to stay with.
   * @param placeholderValue The select's initial value, which is not itself a valid option. Defaults to 'placeholder'.
   */
  public getSelectControlWithPlaceholder(placeholderValue = 'placeholder') {
    const cannotBeInitialValue: ValidatorFn = (control: AbstractControl): ValidationErrors => {
      return control.value === placeholderValue ? { default: true } : null;
    };

    return new FormControl(placeholderValue, [
      Validators.required,
      cannotBeInitialValue
    ]);
  }

  public getEmailControl(initialValue: InitialValue = '', additionalValidators: ValidatorFn[] = []) {
    const isValidEmail: ValidatorFn = (control: AbstractControl): ValidationErrors => {
      return !this.emailValidator.isValid(control.value) ? {pattern: true} : null;
    };

    return new FormControl(initialValue, [
      Validators.required,
      isValidEmail,
      ...additionalValidators
    ]);
  }

  public getPriceControl(initialValue = '') {
    return new FormControl(initialValue, [
      Validators.required,
      Validators.pattern(/^\d*(\.\d{0,3})?$/),
      Validators.min(0.001)
    ]);
  }

  public getDomainControl(initialValue = '') {
    const isValidDomain: ValidatorFn = (control: AbstractControl): ValidationErrors => {
      return !this.domainValidator.isValid(control.value) ? {pattern: true} : null;
    };

    return new FormControl(initialValue, [
      Validators.required,
      isValidDomain,
    ]);
  }

  public getOtpControl(initialValue = '') {
    return new FormControl(initialValue, [
      Validators.required,
      Validators.pattern(/^[0-9]*$/),
      Validators.maxLength(6),
      Validators.minLength(6),
    ]);
  }

  //#region Validators

  /**
   * Validates that the control doesn't contain a string that is just whitespace.
   *
   * @example '' — No errors.
   * @example 'foo' — No errrors.
   * @example '   ' — Error
   *
   * @param control A control to validate
   */
  private notJustWhitespace(control: AbstractControl): ValidationErrors {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    if (value.match(/^\s+$/)) {
      return {whitespace: true};
    }
    return null;
  }

  /**
   * Validates that the control does not start or end with whitespace.
   * @param control A control to validate
   */
  private noWhitespaceWrap(control: AbstractControl): ValidationErrors {
    const value = control.value as string;
    if (!value) {
      return null;
    }
    if (value.match(/^\s/) || value.match(/\s$/)) {
      return {pattern: true};
    }
    return null;
  }

  public validUrl(control: AbstractControl): ValidationErrors {
    if (!control.value) {
      return null;
    }
    try {
      // We use the url constructor to validate a url, it doesn't need to be used as a URL object
      // eslint-disable-next-line no-unused-expressions, @typescript-eslint/no-unused-expressions
      new URL(control.value);
      return null;
    } catch {
      return { pattern: true };
    }
  }

  public validStrictProtocol(control: AbstractControl): ValidationErrors {
    const error = { protocol: true };
    if (!control.value) {
      return null;
    }
    try {
      const url = new URL(control.value);
      if (url.protocol === 'https:') return null;
      if (url.protocol === 'http:' && url.hostname === 'localhost') return null;
      return error;
    } catch {
      return null;
    }
  }

  public validCustomProtocol(control: AbstractControl): ValidationErrors {
    const error = { protocol: true };
    if (!control.value) {
      return null;
    }
    try {
      const url = new URL(control.value);
      if (url.protocol === 'http:' && url.hostname !== 'localhost') return error;
      if (url.protocol === 'ftp:') return error;
      return null;
    } catch {
      return null;
    }
  }

  private validAuthMaxAge(minAuthMaxAgeSeconds = 0, maxAuthMaxAgeSeconds = 604800): ValidatorFn {
    return (group: FormGroup): ValidationErrors => {
      const authMaxAge = AuthMaxAgeInputComponent.inputsToSeconds(group);
      const number = group.get('number');
      const units = group.get('unit');

      const minAuthMaxAge = Duration.fromObject({ seconds: minAuthMaxAgeSeconds });
      const maxAuthMaxAge = Duration.fromObject({ seconds: maxAuthMaxAgeSeconds });

      if (authMaxAge < minAuthMaxAgeSeconds) {
        const error = { minAge: { min: minAuthMaxAge.as(units.value), units: units.value }};
        number.setErrors(error);
        return error;
      } else if (authMaxAge > maxAuthMaxAgeSeconds) {
        const error = { maxAge: { max: maxAuthMaxAge.as(units.value), units: units.value }};
        number.setErrors(error);
        return error;
      } else {
        number.updateValueAndValidity({ onlySelf: true });
        return null;
      }
    };
  }

  //#endregion Validators
}
